import { useCallback, useMemo } from 'react';
import { useValidateDischargeFields } from '@components/Clinicians/Session/v2/Components/DischargeFields';
import { useValidateFaceToFaceFieldset } from '@components/Clinicians/Session/v2/Components/FaceToFaceFieldset';
import { useValidateVirtualConsultationFields } from '@components/Clinicians/Session/v2/Components/BookVirtualConsultationFields';
import type { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import { useSessionOutcome } from './useSessionOutcome';
import { useSessionAppointmentActions } from './useSessionAppointmentActions';

const listFormatter = new Intl.ListFormat('en-GB');

export const useValidateActionTab = () => {
  const { appointmentVersion } = useSessionOutcome();
  const appointmentActions = useSessionAppointmentActions();

  const validators = useActionValidators();
  const actionDescriptions = useActionDescriptions();

  const actionsThatRequireSubtypes = useActionsThatRequireSubtypes();
  const actionsThatAreMandatory = useActionsThatAreMandatory();

  return useCallback(() => {
    if (appointmentVersion !== 2) return { isValid: true, errors: [], invalidMessage: '' };

    const errors: string[] = [];
    const validatedActions = new Set<ApptActionCodes>();

    validators.forEach((validateAction, actionCode) => {
      const actionErrors = validateAction();

      if (actionErrors.length > 0) {
        errors.push(...actionErrors);
        validatedActions.add(actionCode);
      }
    });

    if (Array.isArray(appointmentActions)) {
      for (const action of appointmentActions) {
        if (
          actionsThatRequireSubtypes.includes(action.appt_action_code) &&
          !action.action_subtype_code &&
          !validatedActions.has(action.appt_action_code)
        ) {
          errors.push(`The ${actionDescriptions[action.appt_action_code]} action requires a subtype`);
        }
      }
    }

    if (Array.isArray(actionsThatAreMandatory)) {
      for (const action of actionsThatAreMandatory) {
        if (!appointmentActions.find(({ appt_action_code }) => appt_action_code === action)) {
          errors.push(`The ${actionDescriptions[action]} action is mandatory`);
        }
      }
    }

    const isValid = errors.length === 0;

    const quotedErrors = errors.map((v) => `“${v}”`);
    const invalidMessage = `Please fill in the missing required fields in the “Actions” tab: ${listFormatter.format(quotedErrors)}`;

    return {
      isValid,
      errors,
      invalidMessage
    };
  }, [
    actionDescriptions,
    actionsThatRequireSubtypes,
    appointmentActions,
    appointmentVersion,
    actionsThatAreMandatory,
    validators
  ]);
};

function useActionsThatRequireSubtypes(): ApptActionCodes[] {
  const { availableActions } = useSessionOutcome();

  return useMemo(
    () =>
      availableActions
        .filter((action) => Number(action.action_subtypes?.length) > 0)
        .map((action) => action.appt_action_code),
    [availableActions]
  );
}

function useActionsThatAreMandatory(): ApptActionCodes[] {
  const { availableActions } = useSessionOutcome();

  return useMemo(
    () => availableActions.filter((action) => action.mandatory).map((action) => action.appt_action_code),
    [availableActions]
  );
}

function useActionDescriptions(): Record<ApptActionCodes, string> {
  const { availableActions } = useSessionOutcome();

  return useMemo(
    () =>
      availableActions.reduce(
        (acc, action) => {
          acc[action.appt_action_code] = action.appt_action_desc;
          return acc;
        },
        {} as Record<ApptActionCodes, string>
      ),
    [availableActions]
  );
}

/** Returns an array of error messages or an empty array if the action is valid */
type Validator = () => string[];

function useActionValidators(): Map<ApptActionCodes, Validator> {
  /* For example:
   *
   * const validateFooBarFormItem = useValidateFooBarFormItem();
   * const validateFooBazFormItem = useValidateFooBazFormItem();
   *
   * return useMemo(() => new Map(
   *   [
   *     ['FOOBAR', validateFooBarFormItem],
   *     ['FOOBAZ', validateFooBazFormItem]
   *   ]
   * ), [validatorFooBarFormItem, validatorFooBazFormItem]);
   */

  const validateDischargeFields = useValidateDischargeFields();
  const validateFaceToFaceFieldset = useValidateFaceToFaceFieldset();
  const validateVirtualConsultationFields = useValidateVirtualConsultationFields();

  return useMemo(
    () =>
      new Map([
        ['DISCHG', validateDischargeFields],
        ['REFF2F', validateFaceToFaceFieldset],
        ['BKVCON', validateVirtualConsultationFields]
      ]),
    [validateDischargeFields, validateFaceToFaceFieldset, validateVirtualConsultationFields]
  );
}
